import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

const drift = keyframes`
  0%, 100% { transform: translateY(0) rotate(-1deg); }
  50%      { transform: translateY(-14px) rotate(1deg); }
`;

const blink = keyframes`
  0%, 49%   { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 1; }
`;

/* ---------- end-of-track card ---------- */

const Wrapper = styled(motion.li)`
  list-style: none;
  width: 20rem;
  height: 40vh;
  margin-right: 10rem;
  flex-shrink: 0;
`;

const Floating = styled.div`
  height: 100%;
  padding: 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: ${props => props.theme.text};
  border: 1px dashed ${props => props.theme.text};
  border-radius: 50px 0 50px 0;
  background: radial-gradient(
    circle at 20% 20%,
    rgba(255, 255, 255, 0.08),
    transparent 60%
  );
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.08);
  animation: ${drift} 7s ease-in-out infinite;
`;

const Signal = styled.div`
  font-family: 'Karla', monospace;
  font-size: 0.75rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  opacity: 0.7;

  &::before {
    content: '◉ ';
    animation: ${twinkle} 2s ease-in-out infinite;
  }
`;

const Heading = styled.h2`
  font-size: calc(1em + 0.5vw);
  line-height: 1.3;
`;

const Body = styled.p`
  font-family: 'Karla', sans-serif;
  font-size: calc(0.8em + 0.3vw);
  line-height: 1.5;
  opacity: 0.85;
`;

const Cursor = styled.span`
  display: inline-block;
  width: 0.5em;
  height: 1em;
  margin-left: 0.2em;
  vertical-align: text-bottom;
  background: ${props => props.theme.text};
  animation: ${blink} 1s steps(1) infinite;
`;

const Status = styled.a`
  font-family: 'Karla', monospace;
  font-size: 0.8rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: inherit;
  text-decoration: none;
  border-top: 1px solid ${props => props.theme.text};
  padding-top: 0.6rem;

  &:hover { opacity: 0.7; }
`;

const item = {
    hidden: { scale: 0, opacity: 0 },
    show: {
        scale: 1,
        opacity: 1,
        transition: { type: 'spring', duration: 0.8 },
    },
};

export const ClassifiedNote = ({ contact = 'mailto:your@email.com' }) => (
    <Wrapper variants={item}>
        <Floating>
            <Signal>Incoming transmission</Signal>

            <Heading>
                More missions exist beyond this orbit<Cursor />
            </Heading>

            <Body>
                Client builds and projects from my day job aren't listed here. Many
                sit behind closed doors, but I'm happy to talk through them.
            </Body>

            <Status href={contact}>Status: classified → ask me</Status>
        </Floating>
    </Wrapper>
);

/* ---------- persistent floating hint ---------- */

const Hint = styled.div`
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  max-width: 80vw;
  text-align: center;
  z-index: 1;
  pointer-events: none;
  font-family: 'Karla', monospace;
  font-size: 0.75rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${props => props.theme.text};
  opacity: 0.55;
  animation: ${twinkle} 4s ease-in-out infinite;

  @media (max-width: 600px) {
    font-size: 0.65rem;
    letter-spacing: 0.12em;
  }
`;

export const FloatingHint = () => (
    <Hint>✦ Client &amp; workplace projects not displayed ✦</Hint>
);

export default ClassifiedNote;